import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useStyles } from './ViewPrivateOffer.styles'
import { useSelector, useDispatch } from '../../../store'
import { OfferStatus, PrivateOffer } from '../../../modules/privateOffer/types'
import { IconButton, Typography } from '@mui/material'
import {
  ModalConfirm,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import clsx from 'clsx'
import { dateAndTimeConversion } from '../../../common/utils/dateAndTimeConversion'
import LinkIcon from '@mui/icons-material/Link'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Recipients } from '../Recipients'
import { isEmpty } from 'lodash'
import { CopyToClipboard } from '../../../common/components/CopyToClipboard/CopyToClipboard'
import {
  CopiedToClipboardSuccessMessage,
  CopiedToClipboardUnsuccessfulMessage,
} from '../../../common/utils/messagesContants'
import { isOfferExpired } from '../../../common/utils/isOfferExpired'
import Link from '@mui/material/Link'
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded'
import { updatePrivateOfferForUser } from '../../../modules/privateOffer/action'
import {
  LoadingTypes,
  isLoading,
  updateAppAlert,
  useCurrentCloudProvider,
} from '@labrav/common'
import {
  getKeyCloudAccount,
  getOfferLinkPrivateOffer,
} from '../../../utils/privateOffersListHelpers'
import { OfferAccepted } from '../OfferAccepted'
import { DetailedOffer } from '../DetailedOffer'
import {
  CloudMarketplace,
  ContainerPricingModel,
} from '../../../modules/productsListing/types'
import {
  getAlertMessage,
  getPayloadForCancelPrivateOffer,
  getPrivateOfferStatusMessage,
} from './utils'

export { getOfferLinkPrivateOffer } from '../../../utils/privateOffersListHelpers'
export {
  getPayloadForCancelPrivateOffer,
  isExpiringInNDays,
  type IsExpiringInNDaysProps,
} from './utils'

export type ViewPrivateOfferProps = {
  privateOffer: PrivateOffer
  planType: PlanType
  externalBaseURL?: string
  contractPricingModel?: ContainerPricingModel
}
export const ViewPrivateOffer: React.FC<ViewPrivateOfferProps> = ({
  privateOffer,
  planType,
  externalBaseURL,
  contractPricingModel,
}) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const selectedCloudType = useCurrentCloudProvider() as CloudMarketplace
  const timeZone = useSelector<string>(
    state =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )
  const { modifiedDate: offerCreatedAt, modifiedTime: offerCreationTime } = dateAndTimeConversion(
    privateOffer.createdAt,
    timeZone
  )
  const appAlert = useSelector(state => state.appAlert)
  const offerExpirationDateValue =
    selectedCloudType === 'AZURE'
      ? privateOffer.acceptBy!
      : privateOffer.offerExpirationDate
  const { modifiedDate: offerExpirationDate } = dateAndTimeConversion(
    offerExpirationDateValue,
    timeZone
  )
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false)

  const viewDetailedOffer = useCallback(() => {
    setOpenModal(true)
  }, [])

  const showCancelModal = useCallback(() => {
    setOpenCancelModal(true)
  }, [])

  const handleClose = useCallback(() => {
    setOpenModal(false)
    setOpenCancelModal(false)
  }, [openModal, openCancelModal])

  const handleSubmit = useCallback(async () => {
    const cancelPayload = getPayloadForCancelPrivateOffer(
      selectedCloudType,
      privateOffer
    )
    handleClose()
    await dispatch(
      updatePrivateOfferForUser(privateOffer?.privateOfferId, cancelPayload)
    )
  }, [privateOffer?.privateOfferId])

  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )

  const privateOfferStatusMessage = useMemo(() => {
    return getPrivateOfferStatusMessage({
      status: privateOffer?.offerStatus,
      expirationDate: new Date(offerExpirationDateValue),
    })
  }, [privateOffer?.offerStatus, offerExpirationDateValue])
  const expirationDate = new Date(offerExpirationDateValue)
  const isCancelOfferDisable =
    (privateOffer.offerStatus !== OfferStatus.PRIVATE_OFFER_EMAIL_SENT &&
      privateOffer.offerStatus !== OfferStatus.PRIVATE_OFFER_VIEWED) ||
    isOfferExpired(expirationDate, privateOffer.offerStatus) ||
    loading

  useEffect(() => {
    if (!isEmpty(privateOffer) && !appAlert?.message) {
      const showAlertMessage = getAlertMessage(
        privateOffer?.offerStatus,
        new Date(offerExpirationDateValue)
      )
      dispatch(
        updateAppAlert({
          message: showAlertMessage.message,
          messageType: showAlertMessage.messageType,
          autoClose: true,
        })
      )
    }
  }, [privateOffer?.offerStatus, offerExpirationDateValue])

  const cloudOfferIdKey = getKeyCloudAccount(selectedCloudType)

  const offerLink = getOfferLinkPrivateOffer(selectedCloudType, privateOffer)

  return (
    <>
      <div className={classes.root} data-testid="private-offer-details">
        <div className={classes.offerGeneral}>
          <div
            className={clsx(
              classes.offerGeneralRow,
              classes.offerNameContainer
            )}
          >
            <div className={classes.wrapperOfferName}>
              <Typography
                className={clsx(
                  classes.offerGeneralLabel,
                  classes.offerNameAlign
                )}
                data-testid="private-offer-details-label"
              >
                Offer name
              </Typography>
              <Typography
                className={clsx(
                  classes.offerGeneralValue,
                  classes.offerNameAlign,
                  classes.offerNameValue
                )}
                data-testid="private-offer-details-label-value"
              >
                {privateOffer.privateOfferName}
              </Typography>
            </div>
            <Typography
              className={classes.viewMoreText}
              onClick={viewDetailedOffer}
              data-testid="view-more-details"
            >
              View more details
            </Typography>
          </div>
          <div
            className={clsx(classes.offerGeneralRow, classes.generalRowPadding)}
          >
            <Typography
              className={classes.offerGeneralLabel}
              data-testid="private-offer-details-labra-offer-id-label"
            >
              Labra Offer ID
            </Typography>
            <Typography
              className={classes.offerGeneralValue}
              data-testid="private-offer-details-labra-offer-id-value"
            >
              {privateOffer.privateOfferId}
            </Typography>
          </div>
          <div
            className={
              cloudOfferIdKey && privateOffer[cloudOfferIdKey]
                ? classes.offerGeneralRowDivideContainer
                : classes.offerGeneralRow
            }
          >
            <div
              className={
                cloudOfferIdKey && privateOffer[cloudOfferIdKey]
                  ? classes.offerGeneralRowDivide
                  : ''
              }
            >
              <Typography
                className={classes.offerGeneralLabel}
                data-testid="private-offer-details-company-label"
              >
                Company name
              </Typography>
              <Typography
                className={classes.offerGeneralValue}
                data-testid="private-offer-details-company-value"
              >
                {privateOffer.companyName}
              </Typography>
            </div>
            {cloudOfferIdKey && privateOffer[cloudOfferIdKey] && (
              <div
                className={clsx(
                  classes.offerGeneralRowDivide,
                  classes.noRightBorder
                )}
              >
                <Typography
                  className={classes.offerGeneralLabel}
                  data-testid="private-offer-details-cloud-offer-id-label"
                >
                  {selectedCloudType} Offer ID
                </Typography>
                <Typography
                  className={classes.offerGeneralValue}
                  data-testid="private-offer-details-cloud-offer-id-value"
                >
                  {privateOffer[cloudOfferIdKey]}
                </Typography>
              </div>
            )}
          </div>
          <div className={classes.offerGeneralRowDivideContainer}>
            <div className={classes.offerGeneralRowDivide}>
              <Typography
                className={classes.offerGeneralLabel}
                data-testid="private-offer-created-at"
              >
                Created on
              </Typography>
              <Typography
                className={classes.offerGeneralValue}
                data-testid="private-offer-created-at-value"
              >
                {offerCreatedAt}
              </Typography>
            </div>
            <div
              className={classes.offerGeneralRowDivide}
              style={{ borderRight: '0px' }}
            >
              <Typography
                className={classes.offerGeneralLabel}
                data-testid="private-offer-expiration"
              >
                Offer expiration date
              </Typography>
              <Typography
                className={classes.offerGeneralValue}
                data-testid="private-offer-expiration-value"
              >
                {offerExpirationDate}
              </Typography>
            </div>
          </div>
          {privateOfferStatusMessage && (
            <div
              className={clsx(
                classes.offerGeneralRow,
                classes.generalRowPadding,
                classes.status
              )}
              data-testid="private-offer-status"
            >
              <div>
                <Typography className={classes.offerGeneralLabel}>
                  Offer Status
                </Typography>
                <Typography className={clsx(classes.offerGeneralValue, classes.statusMessage)}>
                  {privateOfferStatusMessage}
                  {privateOffer.offerStatus === OfferStatus.PRIVATE_OFFER_FAILED && (
                    <>
                      <div className={classes.divider} />
                      <div>
                        Issues found: {privateOffer.errorMessage}
                        <br />
                        {offerCreatedAt} || {offerCreationTime}
                      </div>
                    </>
                  )}
                </Typography>
              </div>
            </div>
          )}
          <div className={clsx(classes.offerGeneralRow, classes.linkRow)}>
            <div className={classes.offerUrlContainer}>
              <div className={classes.linkContainer}>
                <LinkIcon
                  className={classes.icon}
                  data-testid="private-offer-link-button"
                />
                <Typography
                  className={clsx(classes.offerGeneralValue, classes.copyLink)}
                  data-testid="private-offer-link"
                >
                  {offerLink ?? 'Link Generation Queued'}
                </Typography>
              </div>
              {!isEmpty(offerLink) && (
                <CopyToClipboard
                  data={offerLink as string}
                  isClickable={!isEmpty(offerLink)}
                  successMessage={CopiedToClipboardSuccessMessage}
                  failureMessage={CopiedToClipboardUnsuccessfulMessage}
                >
                  {!externalBaseURL ? (
                    <IconButton className={classes.iconButton} size="large">
                      <ContentCopyIcon
                        className={`${classes.icon} ${
                          isEmpty(offerLink) ? classes.iconDisabled : ''
                        }`}
                        data-testid="private-offer-link-copy"
                      />
                    </IconButton>
                  ) : (
                    <></>
                  )}
                </CopyToClipboard>
              )}
            </div>
          </div>
          <div className={classes.cancelOfferRow}>
            <Link
              component="button"
              variant="body2"
              className={clsx(
                classes.viewMoreText,
                classes.cancelOfferText,
                isCancelOfferDisable ? classes.disabledButton : ''
              )}
              data-testid="cancel-offer"
              disabled={isCancelOfferDisable}
              onClick={showCancelModal}
            >
              Cancel offer
            </Link>
          </div>
        </div>
        {privateOffer.offerStatus === OfferStatus.PRIVATE_OFFER_ACCEPTED ? (
          <OfferAccepted />
        ) : (
          <Recipients
            recipients={privateOffer.offerRecipients}
            offerStatus={privateOffer.offerStatus}
            timeZone={timeZone}
            offerId={privateOffer.privateOfferId}
            offerExpirationDate={privateOffer.offerExpirationDate}
            planType={planType}
            offerSubscriptionEndDate={privateOffer.subscriptionEndDate || ''}
            isDraft={privateOffer.draft}
            aggrementStartDate={privateOffer.agreementStartDate}
            type={privateOffer.type}
            labraSubscriptionId={privateOffer.labraSubscriptionId}
            subscription={privateOffer.subscription}
          />
        )}
      </div>
      <ModalConfirm
        title={'Preview'}
        subTitle={''}
        content={
          <DetailedOffer
            offer={
              {
                ...privateOffer,
                offerName: privateOffer.privateOfferName,
                stakeholders: privateOffer.offerStakeholders,
              } as unknown as DetailedOffer
            }
            planType={planType}
            isViewPage={true}
            contractPricingModel={contractPricingModel}
          />
        }
        open={openModal}
        titleAcceptButton={'Okay'}
        onAccept={handleClose}
        onDecline={handleClose}
        variant={ModalSizeVariants.medium}
        hideCancelButton={true}
      />
      <ModalConfirm
        title={'Offer Cancellation'}
        subTitle={
          <div data-testid="cancel-offer-alert-content">
            Offer will be cancelled in the {selectedCloudType} Marketplace and
            you will get an email notification. Once offer is cancelled, it
            can't be reversed. <br /> You will have to create a new offer to
            send to the customer.
          </div>
        }
        content={''}
        open={openCancelModal}
        icon={<WarningAmberRounded className={classes.cancelModalIcon} />}
        titleAcceptButton={'Confirm'}
        onAccept={handleSubmit}
        onDecline={handleClose}
        variant={ModalSizeVariants.small}
      />
    </>
  )
}
